import {
  Select as BaseSelect,
  SelectProps as BaseSelectProps,
  Textarea as BaseTextarea,
  TextareaProps as BaseTextareaProps,
  Input,
  InputProps,
  Label,
} from '@kizi/core-components';
import {
  UnitInput as BasicUnitInput,
  CheckboxGroup as CG,
  Checkbox,
  CheckboxGroupProps,
  CheckboxProps,
  IconInput,
  IconInputProps,
  RadioGroup as RG,
  RadioGroupProps,
  UnitInputProps,
} from '@kizi/shared-components';
import React, { useMemo } from 'react';
import { useFieldTracking } from '../../../../utils/hooks';
import { EmailOptional } from '../../../EmailOptional';
import { WithFormProps, withFormProps } from '../../../HOCs/withFormProps';
import { RadioCard as RC } from '../../../RadioCard';
import {
  TileCheckboxGroupOG,
  TileCheckboxGroupProps,
} from '../../../TileCheckboxGroup/TileCheckboxGroup';
import {
  TileRadioGroupOG,
  TileRadioGroupProps,
} from '../../../TileRadioGroup/TileRadioGroup';
import { ReactDatePickerInput } from '../DateInput/DateInput';
const CoreInputWithFormProps = withFormProps(Input);
const IconInputWithFormProps = withFormProps(IconInput);
const UnitInputWithFormProps = withFormProps(BasicUnitInput);
const RadioGroupWithFormProps = withFormProps(RG as any);
const RadioCardWithFormProps = withFormProps(RC as any);
const CheckboxGroupWithFormProps = withFormProps(CG as any);
const CheckboxWithFormProps = withFormProps(Checkbox as any);
const TextareaWithFormProps = withFormProps(BaseTextarea);
const TileCheckboxGroupWithFormProps = withFormProps(
  TileCheckboxGroupOG as any,
);
const TileRadioGroupWithFormProps = withFormProps(TileRadioGroupOG as any);

const BasicInput: React.FC<WithFormProps<IconInputProps>> = (props) => (
  <IconInputWithFormProps
    {...props}
    onBlur={(e) => {
      if (props?.name === 'postalCode') {
        const target = e.target as HTMLInputElement;
        const noSpacesValue = target.value.replace(/\s+/g, '');
        target.value = noSpacesValue;
      }

      if (typeof props.onBlur === 'function' && !props.shouldTriggerAPICall) {
        props.onBlur(e);
      }
      if (props.customBlur) props.customBlur(e);
    }}
    _errorProps={{ variant: 'error', _labelProps: { variant: 'error' } }}
    _successProps={{
      variant: 'success',
      iconProps: {
        fill: 'success',
        name: 'check',
        mx: 3,
        height: 18,
        width: 18,
      },
    }}
  />
);

const Textarea: React.FC<WithFormProps<BaseTextareaProps>> = (props) => (
  <TextareaWithFormProps
    {...props}
    _errorProps={{ variant: 'error', _labelProps: { variant: 'error' } }}
    _successProps={{ variant: 'success' }}
  />
);

const TileCheckboxGroup: React.FC<WithFormProps<TileCheckboxGroupProps>> = (
  props,
) => (
  <TileCheckboxGroupWithFormProps
    {...props}
    _errorProps={{ variant: 'error', _labelProps: { variant: 'error' } }}
    _successProps={{ variant: 'success' }}
  />
);

const TileRadioGroup: React.FC<WithFormProps<TileRadioGroupProps>> = (
  props,
) => (
  <TileRadioGroupWithFormProps
    {...props}
    _errorProps={{ variant: 'error', _labelProps: { variant: 'error' } }}
    _successProps={{ variant: 'success' }}
  />
);

const NoSuccessIconBasicInput: React.FC<WithFormProps<IconInputProps>> = (
  props,
) => {
  const { enhanceInputProps } = useFieldTracking();

  return (
    <CoreInputWithFormProps
      {...enhanceInputProps(props)}
      _errorProps={{ variant: 'error', _labelProps: { variant: 'error' } }}
      _successProps={{ variant: 'success' }}
    />
  );
};

// const DateInput: React.FC<WithFormProps<IconInputProps>> = (props) => (
//   <IconInputWithFormProps
//     {...props}
//     iconProps={{
//       fill: 'disabled',
//       name:
//         props.iconProps?.name || props.iconProps?.name === ''
//           ? props.iconProps?.name
//           : 'calendar',
//       mx: 3,
//       height: 18,
//       width: 18,
//     }}
//     _errorProps={{ variant: 'error', _labelProps: { variant: 'error' } }}
//     _successProps={{
//       variant: 'success',
//       iconProps: {
//         fill: 'success',
//         name: 'check',
//         mx: 3,
//         height: 18,
//         width: 18,
//       },
//     }}
//   />
// );

const NoValidationInput: React.FC<WithFormProps<InputProps>> = (props) => (
  <CoreInputWithFormProps {...props} />
);

const UnitInput: React.FC<WithFormProps<UnitInputProps>> = (props) => (
  <UnitInputWithFormProps
    _errorProps={{
      variant: 'error',
      _labelProps: { variant: 'error' },
      unit: props.unit,
    }}
    _successProps={{ variant: 'success', unit: props.unit }}
    unitProps={{ mx: 3 }}
    {...props}
  />
);
export interface SelectOption {
  label: string;
  value: string | number;
  attrs?: JSX.IntrinsicElements['option'];
}

export interface SelectProps extends BaseSelectProps {
  options: SelectOption[];
  _options?: SelectOption[];
  selectCustomOnChange?: (e) => React.ChangeEvent<HTMLInputElement>;
}

export const SelectWithOptions: React.FC<SelectProps> = ({
  options,
  _options = [],
  ...props
}) => {
  const optionsJsx = useMemo(
    () =>
      (options ?? _options).map((o, i) => (
        <option key={'option-' + i} value={o.value} {...o.attrs}>
          {o.label}
        </option>
      )),
    [options, _options],
  );

  return (
    <BaseSelect
      {...props}
      onChange={(e) => {
        if (typeof props.onChange === 'function') {
          props.onChange(e);
          props.selectCustomOnChange && props.selectCustomOnChange(e);
        }
      }}
    >
      {optionsJsx}
    </BaseSelect>
  );
};
const SelectWithFormProps = withFormProps(SelectWithOptions);
const Select = (props) => {
  const { enhanceInputProps } = useFieldTracking();
  return (
    <SelectWithFormProps
      {...props}
      {...enhanceInputProps(props)}
      _errorProps={{ variant: 'error' }}
      _successProps={{ variant: 'success' }}
    />
  );
};

const handleRadioMerge = (radios, _options) => {
  return radios
    ? radios.map((radio, i) => {
        if (_options && i < _options.length)
          return { ...radio, ..._options[i] };
        return radio;
      })
    : _options;
};

const DefaultRadioGroup = ({
  _options,
  radios,
  ...props
}: WithFormProps<RadioGroupProps>) => {
  const mergedRadios = handleRadioMerge(radios, _options);

  return (
    <RadioGroupWithFormProps
      containerProps={{
        flexDirection: 'row-reverse',
        justifyContent: 'flex-end',
        mt: 1,
      }}
      _labelProps={{ mt: 2 }}
      _errorProps={{ variant: 'error' }}
      _successProps={{ variant: 'success' }}
      {...props}
      onChange={(e) => {
        if (typeof props.onChange === 'function') {
          props.onChange(e);
          props.customRadioChange && props.customRadioChange(e);
        }
      }}
      radios={mergedRadios}
    />
  );
};

const DefaultRadioCard = ({
  _options,
  radios,
  ...props
}: WithFormProps<RadioGroupProps>) => {
  const mergedRadios = handleRadioMerge(radios, _options);
  return (
    <RadioCardWithFormProps
      containerProps={{
        flexDirection: 'row-reverse',
        justifyContent: 'flex-end',
        mt: 1,
      }}
      _labelProps={{ mt: 2 }}
      _errorProps={{ variant: 'error' }}
      _successProps={{ variant: 'success' }}
      {...props}
      radios={mergedRadios}
    />
  );
};

const DefaultCheckbox = (props: WithFormProps<CheckboxProps>) => {
  return (
    <CheckboxWithFormProps
      _errorProps={{ variant: 'error' }}
      _successProps={{ variant: 'success' }}
      {...props}
      onChange={(e) => {
        if (typeof props.onChange === 'function') {
          props.onChange(e);
          props.customCheckboxChange && props.customCheckboxChange(e);
        }
      }}
    />
  );
};

const DefaultCheckboxGroup = ({
  ...props
}: WithFormProps<CheckboxGroupProps>) => {
  return (
    <CheckboxGroupWithFormProps
      _labelProps={{ my: 0 }}
      _errorProps={{ variant: 'error' }}
      _successProps={
        props.variant === 'tile'
          ? { variant: props.variant }
          : { variant: 'success' }
      }
      {...props}
    />
  );
};

const LabelWithTooltip = withFormProps(Label);

export {
  BasicInput,
  DefaultCheckbox as Checkbox,
  DefaultCheckboxGroup as CheckboxGroup,
  ReactDatePickerInput as DateInput,
  // DateInput,
  DefaultCheckbox,
  DefaultRadioCard,
  DefaultRadioGroup,
  EmailOptional,
  LabelWithTooltip,
  NoSuccessIconBasicInput,
  NoValidationInput,
  DefaultRadioCard as RadioCard,
  DefaultRadioGroup as RadioGroup,
  Select,
  Textarea,
  TileCheckboxGroup,
  TileRadioGroup,
  UnitInput,
};
